/**
 * Bounded payload for native desktop notifications. The website renderer
 * forwards new unread platform notifications through the preload bridge;
 * Electron main validates them again before showing anything. A notification
 * only opens its chat. It never approves or executes an action.
 */
export const DESKTOP_NOTIFICATION_TITLE_MAX = 80;
export const DESKTOP_NOTIFICATION_BODY_MAX = 180;
export const DESKTOP_NOTIFICATION_DEEP_LINK_MAX = 512;
export const DESKTOP_NOTIFICATION_EVENT_ID_MAX = 128;
export const DESKTOP_NOTIFICATION_BADGE_MAX = 999;

export const DESKTOP_NOTIFICATION_STATE_CHANNEL = 'imai:notification-state:v1';
export const DESKTOP_NOTIFICATION_SHOW_CHANNEL = 'imai:notification-show:v1';
export const DESKTOP_NOTIFICATION_BADGE_CHANNEL = 'imai:notification-badge:v1';
export const DESKTOP_NOTIFICATION_SETTINGS_CHANNEL =
  'imai:notification-settings-open:v1';
/** Main → renderer: the user clicked a notification for this deep link. */
export const DESKTOP_NOTIFICATION_OPEN_CHANNEL = 'imai:notification-open:v1';

export type DesktopNotificationItem = {
  eventId: string;
  title: string;
  body: string;
  deepLink: string;
};

/** What the operating system lets this app do; never the user's opt-out. */
export type DesktopNotificationNativeState =
  | 'available'
  | 'blocked'
  | 'unsupported';

export type DesktopNotificationShowResult =
  | 'shown'
  | 'duplicate'
  | 'suppressed'
  | 'rate_limited'
  | 'unavailable';

const ITEM_KEYS = ['eventId', 'title', 'body', 'deepLink'];
const EVENT_ID = /^[A-Za-z0-9_:-]+$/;

/** Same-origin relative path: starts with one slash, no backslash or controls. */
export function isSafeRelativeDeepLink(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= DESKTOP_NOTIFICATION_DEEP_LINK_MAX &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\') &&
    !/[\u0000-\u001f\u007f]/.test(value)
  );
}

function boundedText(value: unknown, max: number): value is string {
  return (
    typeof value === 'string' && value.trim().length > 0 && value.length <= max
  );
}

/** Strict check used by Electron main and the preload: exact keys, caps. */
export function parseDesktopNotificationItem(
  value: unknown
): DesktopNotificationItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const keys = Object.keys(value);
  if (
    keys.length !== ITEM_KEYS.length ||
    keys.some((key) => !ITEM_KEYS.includes(key))
  )
    return null;
  const item = value as Record<string, unknown>;
  if (
    typeof item.eventId !== 'string' ||
    item.eventId.length === 0 ||
    item.eventId.length > DESKTOP_NOTIFICATION_EVENT_ID_MAX ||
    !EVENT_ID.test(item.eventId) ||
    !boundedText(item.title, DESKTOP_NOTIFICATION_TITLE_MAX) ||
    !boundedText(item.body, DESKTOP_NOTIFICATION_BODY_MAX) ||
    !isSafeRelativeDeepLink(item.deepLink)
  )
    return null;
  return {
    eventId: item.eventId,
    title: item.title,
    body: item.body,
    deepLink: item.deepLink,
  };
}

function truncate(value: string, max: number) {
  const text = value.trim();
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** Renderer-side shaping: trims copy to the caps; unsafe links are dropped. */
export function toDesktopNotificationItem(input: {
  eventId: string;
  title: string;
  body: string;
  deepLink: string;
}): DesktopNotificationItem | null {
  return parseDesktopNotificationItem({
    eventId: input.eventId,
    title: truncate(input.title, DESKTOP_NOTIFICATION_TITLE_MAX),
    body: truncate(input.body, DESKTOP_NOTIFICATION_BODY_MAX),
    deepLink: input.deepLink,
  });
}

export function parseDesktopNotificationBadge(value: unknown): number | null {
  return typeof value === 'number' &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= DESKTOP_NOTIFICATION_BADGE_MAX
    ? value
    : null;
}

export function isDesktopNotificationNativeState(
  value: unknown
): value is DesktopNotificationNativeState {
  return (
    value === 'available' || value === 'blocked' || value === 'unsupported'
  );
}

export function isDesktopNotificationShowResult(
  value: unknown
): value is DesktopNotificationShowResult {
  return (
    value === 'shown' ||
    value === 'duplicate' ||
    value === 'suppressed' ||
    value === 'rate_limited' ||
    value === 'unavailable'
  );
}
